import Phaser from 'phaser'
import MouseTileMarker from './MouseTileMarker'
import ScrapDisplay from './ScrapDisplay'

type PlatformBuildOptions = {
  buildEnabled: boolean
  removeEnabled: boolean
  buildTileIndex?: number
  onlyRemovePlayerMade?: boolean
  showMarker?: boolean
  scrapDisplay?: ScrapDisplay
  buildCost?: number
  removeReward?: number
}

export default class PlatformBuildController {
  private scene: Phaser.Scene
  private groundLayer: Phaser.Tilemaps.TilemapLayer
  private marker?: MouseTileMarker
  private buildEnabled: boolean
  private removeEnabled: boolean
  private buildTileIndex: number
  private onlyRemovePlayerMade: boolean
  private scrapDisplay?: ScrapDisplay
  private buildCost: number
  private removeReward: number

  constructor(
    scene: Phaser.Scene,
    map: Phaser.Tilemaps.Tilemap,
    groundLayer: Phaser.Tilemaps.TilemapLayer,
    options: PlatformBuildOptions,
  ) {
    this.scene = scene
    this.groundLayer = groundLayer
    this.buildEnabled = options.buildEnabled
    this.removeEnabled = options.removeEnabled
    this.buildTileIndex = options.buildTileIndex ?? 353
    this.onlyRemovePlayerMade = options.onlyRemovePlayerMade ?? true
    this.scrapDisplay = options.scrapDisplay
    this.buildCost = options.buildCost ?? 1
    this.removeReward = options.removeReward ?? 1

    if (options.showMarker !== false) {
      this.marker = new MouseTileMarker(scene, map, this.buildTileIndex)
    }

    // Let right-click interact with the game instead of opening the browser context menu.
    this.scene.input.mouse?.disableContextMenu()
  }

  update(): void {
    this.marker?.update()

    const pointer = this.scene.input.activePointer
    if (!pointer.isDown) {
      return
    }

    const worldPoint = pointer.positionToCamera(this.scene.cameras.main) as Phaser.Math.Vector2
    const pos = this.groundLayer.worldToTileXY(worldPoint.x, worldPoint.y)
    const currentTile = this.groundLayer.getTileAt(pos.x, pos.y)

    const isRemoveAction = this.removeEnabled && pointer.rightButtonDown()
    if (isRemoveAction) {
      if (!currentTile) {
        return
      }

      const canRemove =
        !this.onlyRemovePlayerMade ||
        Boolean(currentTile.properties?.playerMade)

      if (canRemove) {
        this.groundLayer.removeTileAtWorldXY(worldPoint.x, worldPoint.y)
        if (this.scrapDisplay) {
          this.scrapDisplay.addScrap(this.removeReward)
        }
      }
      return
    }

    if (!this.buildEnabled || !pointer.leftButtonDown() || currentTile) {
      return
    }

    // Check if we have enough scrap to build
    if (this.scrapDisplay && !this.scrapDisplay.subtractScrap(this.buildCost)) {
      return
    }

    const tile = this.groundLayer.putTileAtWorldXY(this.buildTileIndex, worldPoint.x, worldPoint.y)
    if (!tile) {
      // Refund scrap if tile placement failed
      if (this.scrapDisplay) {
        this.scrapDisplay.addScrap(this.buildCost)
      }
      return
    }

    tile.setCollision(true)
    tile.properties.playerMade = true
  }

  createPlayerMadeTileAt(x: number, y: number): void {
    const tile = this.groundLayer.putTileAtWorldXY(this.buildTileIndex, x, y)
    if (!tile) {
      return
    }

    tile.setCollision(true)
    tile.properties.playerMade = true
  }

  destroy(): void {
    this.marker?.destroy()
  }
}
